import React, { useState } from 'react';
import { Store, FileText, CheckCircle, ArrowRight, ArrowLeft, Mail, Lock, X, DollarSign, Users, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './SellerVerification.css';

interface FormData {
  // Step 1: Business Information
  businessName: string;
  businessType: string;
  country: string;
  // Step 2: Personal Information
  fullName: string;
  email: string;
  phone: string;
  // Step 3: Verification Documents
  businessLicense: File | null;
  idProof: File | null;
  // Login
  loginEmail: string;
  loginPassword: string;
}

const SellerVerification: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    businessType: 'individual',
    country: '',
    fullName: '',
    email: '',
    phone: '',
    businessLicense: null,
    idProof: null,
    loginEmail: '',
    loginPassword: '',
  });

  // Swipe functionality state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Handle final submission
    console.log('Form submitted:', formData);
    setShowSuccess(true);

    // Redirect to home page after 5 seconds
    setTimeout(() => {
      navigate('/');
    }, 10000);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login:', formData.loginEmail);
    alert('Login functionality to be implemented');
  };

  // Swipe handlers
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    if (touchStart !== null) {
      const offset = e.targetTouches[0].clientX - touchStart;
      setSwipeOffset(offset);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentStep < 2) {
      handleNext();
    } else if (isRightSwipe && currentStep > 0) {
      handleBack();
    }

    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const steps = [
    { icon: Store, title: 'Business Info', description: 'Tell us about your business' },
    { icon: FileText, title: 'Personal Details', description: 'Your contact information' },
    { icon: CheckCircle, title: 'Verification', description: 'Upload required documents' },
  ];

  // Render success screen
  if (showSuccess) {
    return (
      <div className="success-screen">
        <div className="success-animation">
          <div className="success-circle">
            <CheckCircle className="success-icon" />
          </div>
        </div>
        <h1 className="success-title">Thank You for Registering with Us!</h1>
        <p className="success-message">
          Welcome home! You're now part of our family of sellers.
          <br />
        </p>
        <p className="success-email-note">
          Check your email to complete the verification process
        </p>
      </div>
    );
  }

  // Render login form
  if (showLogin) {
    return (
      <div className="seller-verification-page">
        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
          className="close-button"
          aria-label="Close"
        >
          <X className="icon-sm" />
        </button>

        <div className="split-container">
          {/* Left Card - Login Form */}
          <div className="card login-card">
            <div className="card-content">
              <button
                onClick={() => setShowLogin(false)}
                className="back-button"
              >
                <ArrowLeft className="icon-sm" />
                Back
              </button>

              <h1 className="card-title">Welcome Back</h1>
              <p className="card-subtitle">Sign in to your seller account</p>

              <form onSubmit={handleLogin} className="login-form">
                <div className="form-group">
                  <label className="form-label">
                    <Mail className="icon-sm" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="loginEmail"
                    value={formData.loginEmail}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="seller@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Lock className="icon-sm" />
                    Password
                  </label>
                  <input
                    type="password"
                    name="loginPassword"
                    value={formData.loginPassword}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your password"
                    required
                  />
                </div>

                <button type="submit" className="btn-primary">
                  Sign In
                  <ArrowRight className="icon-sm" />
                </button>

                <div className="form-footer">
                  <button type="button" onClick={() => {}} className="link">Forgot password?</button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Card - Image */}
          <div className="card image-card">
            <img
              src="/sellerlogin.jpg"
              alt="Seller Login"
              className="login-image"
            />
          </div>
        </div>
      </div>
    );
  }

  // Render verification steps
  return (
    <div className="seller-verification-page">
      {/* Close Button */}
      <button
        onClick={() => navigate('/')}
        className="close-button"
        aria-label="Close"
      >
        <X className="icon-sm" />
      </button>

      <div className="split-container">
        {/* Left Card - Form Steps */}
        <div
          className="card form-card"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: swipeOffset === 0 ? 'transform 0.3s ease' : 'none'
          }}
        >
          <div className="card-content">
            {/* Upper Section - Steps & Form */}
            <div className="upper-section">
            {/* Progress Steps */}
            <div className="steps-header">
              <h1 className="steps-title">Seller Verification</h1>
              <div className="steps-indicator" data-step={currentStep}>
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={index} className={`step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}>
                      <div className="step-circle">
                        <Icon className="step-icon" />
                      </div>
                      <div className="step-label">{step.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 1: Business Information */}
            {currentStep === 0 && (
              <div className="form-section">
                <h2 className="form-title">Business Information</h2>

                <div className="form-group">
                  <label className="form-label">Business Name</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your business name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Business Type</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="individual">Individual Seller</option>
                    <option value="company">Registered Company</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your country"
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 2: Personal Information */}
            {currentStep === 1 && (
              <div className="form-section">
                <h2 className="form-title">Personal Details</h2>

                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="+1 234 567 8900"
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 3: Document Upload */}
            {currentStep === 2 && (
              <div className="form-section">
                <h2 className="form-title">Verification Documents</h2>

                <div className="form-group">
                  <label className="form-label">Business License/Registration</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'businessLicense')}
                    className="form-input file-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <p className="form-help">Upload your business registration or license (PDF, JPG, PNG)</p>
                </div>

                <div className="form-group">
                  <label className="form-label">Government ID Proof</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'idProof')}
                    className="form-input file-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <p className="form-help">Upload your passport, driver's license, or national ID</p>
                </div>

                <div className="info-box">
                  <CheckCircle className="icon-sm" />
                  <p>All documents are securely encrypted and stored. We'll review your application within 24-48 hours.</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="form-actions">
              {currentStep > 0 && (
                <button onClick={handleBack} className="btn-secondary">
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
              )}

              {currentStep < 2 ? (
                <button onClick={handleNext} className="btn-primary">
                  Next
                  <ArrowRight className="icon-sm" />
                </button>
              ) : (
                <button onClick={handleSubmit} className="btn-primary">
                  Submit Application
                  <CheckCircle className="icon-sm" />
                </button>
              )}
            </div>

            <div className="form-footer">
              Already have an account?{' '}
              <button onClick={() => setShowLogin(true)} className="link">
                Sign In
              </button>
            </div>

            {/* Swipe Hint */}
            {currentStep > 0 && currentStep < 2 && (
              <div className="swipe-hint">
                Swipe or use buttons to navigate
              </div>
            )}
            </div>

            {/* Lower Section - Company Offerings */}
            <div className="company-offerings">
              <h3 className="offerings-title">Why Sell With Us?</h3>
              <div className="offerings-grid">
                <div className="offering-item">
                  <div className="offering-icon">
                    <DollarSign className="icon-xs" />
                  </div>
                  <span className="offering-text">3 - 5% Fees</span>
                </div>
                <div className="offering-item">
                  <div className="offering-icon">
                    <Users className="icon-xs" />
                  </div>
                  <span className="offering-text">5K+ Buyers</span>
                </div>
                <div className="offering-item">
                  <div className="offering-icon">
                    <Zap className="icon-xs" />
                  </div>
                  <span className="offering-text">Instant Payouts</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card - Image */}
        <div className="card image-card">
          <img
            src="/sellerlogin.jpg"
            alt="Seller"
            className="verification-image"
          />
        </div>
      </div>
    </div>
  );
};

export default SellerVerification;
